import { Link } from "react-router-dom";
import { ArrowRight, Search, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import VehicleFinder from "./VehicleFinder";

export default function Hero() {
  return (
    <section data-testid="home-hero" className="relative bg-[#0B0F19] text-white overflow-hidden -mt-16 lg:-mt-20 pt-24 lg:pt-32 pb-16 lg:pb-24">
      {/* Layered background */}
      <div className="absolute inset-0 ici-grid-bg-dark opacity-40 pointer-events-none" />
      <div className="absolute inset-0 ici-radial pointer-events-none" />
      <div
        className="absolute inset-0 opacity-25 pointer-events-none mix-blend-luminosity"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/159293/car-engine-motor-clean-customized-159293.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center right",
          maskImage: "linear-gradient(to left, black 40%, transparent 90%)",
          WebkitMaskImage: "linear-gradient(to left, black 40%, transparent 90%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-4 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 ici-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-300">Precision Fuel Delivery</span>
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-[1.02] text-white">
              Precision Fuel Delivery.<br />
              <span className="text-[#0055FF]">Dependable Performance.</span>
            </h1>
            <p className="mt-6 text-slate-300 text-base lg:text-lg max-w-xl leading-relaxed">
              Shop OEM-quality fuel injectors, tested replacement components, and application-specific automotive parts for
              domestic, European, and Asian vehicles.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild data-testid="hero-cta-find" className="bg-[#0055FF] hover:bg-[#0044CC] h-12 px-6 text-base">
                <Link to="/vehicle-finder"><Zap className="w-4 h-4 mr-1.5" /> Find Your Injectors</Link>
              </Button>
              <Button asChild data-testid="hero-cta-partnumber" variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10 h-12 px-6 text-base">
                <Link to="/search/part"><Search className="w-4 h-4 mr-1.5" /> Search by Part Number</Link>
              </Button>
              <a
                data-testid="hero-cta-ebay"
                href="https://www.ebay.com/usr/intercorp_industries"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-1 ml-1"
              >
                Shop Our eBay Store <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 relative ici-fade-up" style={{ animationDelay: "180ms" }}>
            <div className="relative aspect-square lg:aspect-[4/5] ici-light-sweep">
              <div className="absolute inset-6 rounded-2xl bg-gradient-to-br from-[#0055FF]/20 via-transparent to-slate-900/30 blur-2xl" />
              <div className="relative w-full h-full rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-xl overflow-hidden">
                <img
                  src="https://i.ebayimg.com/images/g/PfYAAeSwANdp4X1f/s-l1600.webp"
                  alt="OEM ACDelco Fuel Injectors Set"
                  className="absolute inset-0 w-full h-full object-contain p-6"
                />
                {/* Floating spec labels */}
                <div className="absolute top-6 left-6 bg-black/50 backdrop-blur border border-white/10 rounded-md px-3 py-1.5">
                  <div className="text-[9px] font-mono text-slate-400 uppercase tracking-widest">Part No</div>
                  <div className="text-sm font-mono text-white">A12668390</div>
                </div>
                <div className="absolute bottom-6 right-6 bg-black/50 backdrop-blur border border-white/10 rounded-md px-3 py-1.5">
                  <div className="text-[9px] font-mono text-slate-400 uppercase tracking-widest">Engine</div>
                  <div className="text-sm font-mono text-white">5.3L V8</div>
                </div>
                <div className="absolute top-1/2 right-6 -translate-y-1/2 bg-[#0055FF]/90 backdrop-blur rounded-md px-3 py-1.5 text-white">
                  <div className="text-[9px] font-mono uppercase tracking-widest opacity-80">OEM</div>
                  <div className="text-sm font-mono">ACDelco</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vehicle finder */}
        <div className="mt-10 lg:mt-16 ici-fade-up" style={{ animationDelay: "300ms" }}>
          <VehicleFinder variant="hero" />
        </div>

        {/* Trust strip */}
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-6 pt-8 border-t border-white/10">
          {[
            { k: "Nationwide", v: "USPS · UPS shipping" },
            { k: "Vehicle-verified", v: "Fitment on every listing" },
            { k: "Real inventory", v: "Live eBay sync" },
            { k: "Direct support", v: "Fitment help by email" },
          ].map((s) => (
            <div key={s.k}>
              <div className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">{s.k}</div>
              <div className="text-sm text-slate-200 mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
